// components/ProjectList.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  Badge,
  Tooltip,
} from "@mui/material";
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';

interface ProjectListProps {
  projects: any[];
  selectedProject: any;
  onProjectSelect: (project: any) => void;
  currentProjectId: string;
  tasksFromProjects?: string[]; // Array of project IDs that have tasks added
}

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  selectedProject,
  onProjectSelect,
  currentProjectId,
  tasksFromProjects = [],
}) => {
  const { t } = useTranslation(["bugherd"]);

  // Check if project has tasks added
  const hasTasksFromProject = (projectId: string) => {
    return tasksFromProjects.includes(projectId);
  };

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      {/* All Projects Section */}
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, fontSize: "14px", ml: 2, fontWeight: "bold" }}>
          {t("Select project")}
        </Typography>

        <List sx={{ p: 0 }}>
          {projects.map((project) => (
            <ListItem
              key={project._id}
              button
              onClick={() => onProjectSelect(project)}
              selected={selectedProject?._id === project._id}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "#e3f2fd",
                },
                // Highlight if project has tasks added
                ...(hasTasksFromProject(project._id) && {
                  backgroundColor:
                    selectedProject?._id === project._id
                      ? "#e3f2fd"
                      : "rgba(25, 118, 210, 0.08)",
                  border: "1px solid rgba(25, 118, 210, 0.3)",
                }),
                // Highlight current project
                ...(project._id === currentProjectId && {
                  backgroundColor:
                    selectedProject?._id === project._id
                      ? "#e3f2fd"
                      : "rgba(245, 184, 66, 0.08)",
                  border:
                    selectedProject?._id === project._id
                      ? "1px solid rgba(25, 118, 210, 0.5)"
                      : "1px solid rgba(245, 184, 66, 0.5)",
                }),
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Radio
                  checked={selectedProject?._id === project._id}
                  size="small"
                  color="success"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {hasTasksFromProject(project._id) && (
                      <Tooltip 
                        title={t("Has dependencies")} 
                        arrow 
                        placement="right"
                      >
                        <AccountTreeOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: "primary.main",
                            opacity: 0.8,
                            ml: 0.5,
                          }}
                        />
                      </Tooltip>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          selectedProject?._id === project._id ? 600 : 400,
                        color:'text.secondary',
                        fontSize: "14px",
                        flex: 1, // Take remaining space
                      }}
                    >
                      {project.name}
                      {project._id === currentProjectId && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.75rem",
                            color: "text.secondary",
                            ml: 0.5,
                          }}
                        >
                          {t("(Current)")}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default ProjectList;
